import React from 'react';
import { Box, Typography } from '@mui/material';
import { panelShadow } from '../../../theme/styleHelpers';

export type InfoCardProps = {
  title: string;
  subheader?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  elevation?: number;
  sx?: Record<string, any>;
  headerSx?: Record<string, any>;
  contentSx?: Record<string, any>;
  onClick?: () => void;
  /** 'ai' adds a subtle cyan tint and uses brand-teal/cyan for the title. */
  variant?: 'default' | 'ai';
};

const InfoCard: React.FC<InfoCardProps> = ({
  title,
  subheader,
  icon,
  children,
  actions,
  // elevation is preserved for API compatibility but no longer applied;
  // the new design uses a fixed panelShadow from theme/styleHelpers.
  elevation: _elevation, /* eslint-disable-line @typescript-eslint/no-unused-vars */
  sx = {},
  headerSx = {},
  contentSx = {},
  onClick,
  variant = 'default',
}) => {
  const isAi = variant === 'ai';

  return (
    <Box
      onClick={onClick}
      sx={[
        (theme) => ({
          background: isAi
            ? `linear-gradient(0deg, rgba(34,181,220,.04), rgba(34,181,220,.04)), ${theme.palette.background.paper}`
            : theme.palette.background.paper,
          border: `1px solid ${isAi ? 'rgba(34,181,220,.18)' : theme.palette.divider}`,
          borderRadius: '14px',
          boxShadow: panelShadow(theme),
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: onClick ? 'pointer' : 'default',
        }),
        sx,
      ]}
    >
      {(title || icon || actions) && (
        <Box
          sx={[
            (theme) => ({
              padding: '14px 18px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderBottom: `1px solid ${theme.palette.divider}`,
            }),
            headerSx,
          ]}
        >
          {icon && (
            <Box
              sx={(theme) => ({
                color: isAi
                  ? theme.palette.mode === 'light'
                    ? theme.palette.brand.teal
                    : theme.palette.brand.cyan
                  : theme.palette.brand.teal,
                display: 'flex',
              })}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            {title && (
              <Typography
                component="div"
                sx={(theme) => ({
                  fontSize: 13,
                  fontWeight: 700,
                  color: isAi
                    ? theme.palette.mode === 'light'
                      ? theme.palette.brand.teal
                      : theme.palette.brand.cyan
                    : theme.palette.text.primary,
                })}
              >
                {title}
              </Typography>
            )}
            {subheader && (
              <Typography
                component="div"
                sx={(theme) => ({
                  fontSize: 12,
                  color: theme.palette.text.secondary,
                  marginTop: '2px',
                })}
              >
                {subheader}
              </Typography>
            )}
          </Box>
          {actions && <Box sx={{ marginLeft: 'auto' }}>{actions}</Box>}
        </Box>
      )}
      <Box sx={[{ flexGrow: 1, padding: '14px 18px' }, contentSx]}>{children}</Box>
    </Box>
  );
};

export default InfoCard;
